import type { AutoQaRatingCategory } from '@zendesk/zqa-services/autoqa'
import type { Rating as RatingTypeFromServices, RatingRootCause } from '@zendesk/zqa-services/reviews'
import type {
  AdvancedScorecard_RootCauses as RootCauses,
  AdvancedScorecard_ScorecardRatingCategoryRating as CategoryRating,
} from '@zendesk/zqa-services/scorecards'
import { type Scale, type ScaleType, SCALE_VALUES } from '@/data/scales'
import type { Scorecard } from '@/modules/shared/TicketContent/types/scorecard'
import { type RootCause, RequireReasonVisibility, AutoQaCategoryType } from '@/types/category'
import i18n from '@/i18n'
import { translationExists } from '@/i18n/utils'

import { hasSelectableRootCauseId } from './singleSelectRootCauses'

export {
  getRetainedSingleSelectRootCause,
  getSingleSelectRootCauseSummary,
  hasSelectableRootCauseId,
} from './singleSelectRootCauses'

/**
 * This utils module should house functional methods to better unify and
 * re-use the different ratings and grouping methods used in:
 *
 * Feedback.vue
 * ReviewSidebar.vue (TicketContent)
 * Review.vue (extension)
 */

// Golang services types
export interface Category {
  categoryDescription?: string
  categoryId: number
  categoryName: string
  critical: boolean
  reviewCritical: boolean
  groupCritical: boolean
  groupId?: number | string
  groupName?: string
  groupPosition?: number
  isArchived: boolean
  multipleRequireReasons: boolean
  position: number
  requireReason: boolean
  requireReasonVisibility: RequireReasonVisibility
  autoQaApplicabilities: string[]
  freeTextAllowed: boolean
  rootCauses: RootCause[]
  scale: ScaleType
  scorecards: Scorecard[]
  weight: number
  autoQaCategory?: AutoQaCategoryType
  autoQaCustomCategoryTemplateId?: string
  autoQaRatingCategory?: AutoQaRatingCategory
  advancedRootCauses?: RootCauses
  skippable: boolean
  isAdvancedScorecardCategory?: boolean
  scorecardId?: string
  categoryRatings: CategoryRating[]
  status?: string
  evaluated?: boolean
  skipReason?: string
}

export interface Rating extends Category {
  rating?: RatingValue
  cause?: string[] | null
  rootCause?: RatingRootCause
  failCategory?: boolean
  scorecardId?: string
}

export interface RatingRequestPayload {
  categoryId: number
  rating: number | null | undefined
  cause?: string[] | null
  rootCause?: RatingRootCause
}

export interface CategoryGroup {
  id: number
  name: string
  position: number
  categories: Rating[]
}

export type GivenRatings = Record<string, Rating>

export type RatingFields = Pick<Rating, 'categoryId' | 'cause' | 'rootCause'> & { rating?: RatingValue | null }

interface DraftRatingFields extends Omit<RatingFields, 'cause' | 'rating'> {
  cause: string[]
  rating?: RatingValue
}

// Marks an "N/A" rating in form state if the Account-level Scale settings allow it.
// The API represents N/A as `null`, see `toApiRating` and `fromApiRating`.
export const RATING_NA = 'NA'
export type RatingNA = typeof RATING_NA
export type RatingValue = number | RatingNA

// TODO(ZQAZOS-704): remove once the backend no longer returns 42 for N/A ratings
const LEGACY_RATING_NA = 42

export const isNaRating = (rating: unknown): rating is RatingNA => rating === RATING_NA

/**
 * Maps a submitted rating from an API response to form state. `null` means the category was rated N/A.
 */
export const fromApiRating = (rating: number | null | undefined): RatingValue | undefined => {
  if (rating === null) return RATING_NA
  // TODO(ZQAZOS-704): remove once the backend no longer returns 42 for N/A ratings
  if (rating === LEGACY_RATING_NA) return RATING_NA

  return rating
}

/**
 * Maps a form state rating to the API request value. N/A is sent as `null`.
 */
export const toApiRating = (rating: RatingValue | null | undefined): number | null | undefined => {
  if (isNaRating(rating)) return null

  return rating ?? undefined
}

/**
 * AutoQA returns its rating as a string, and `null` when it rated the category N/A.
 */
export const isAutoQaNaRating = (rating: string | null | undefined) =>
  // TODO(ZQAZOS-704): remove the '42' check once the backend no longer returns 42 for N/A AutoQA ratings
  rating === null || rating === String(LEGACY_RATING_NA)

export const fromAutoQaRating = (rating: string | null | undefined): RatingValue | undefined => {
  if (isAutoQaNaRating(rating)) return RATING_NA
  if (rating === undefined || rating === '') return undefined

  return Number(rating)
}

export const getScorecardId = (scorecard?: Scorecard | string) =>
  typeof scorecard === 'string' ? scorecard : scorecard?.id

export const populateWithNA = (givenRatings: GivenRatings): Rating[] =>
  Object.values(givenRatings).map((r) => {
    if (r.skippable) {
      return { ...r, rating: r.rating ?? RATING_NA }
    } else {
      return r
    }
  })

/**
 * Keeps selected rating values when the same review context rebuilds category metadata.
 * Nullish rating values mean "not rated" and are not copied; other runtime values are preserved.
 */
export const preserveExistingRatingValues = <T extends Record<number, RatingFields>>(
  blankRatings: T,
  previousRatings: Record<number, RatingFields>,
): T => {
  return Object.fromEntries(
    Object.entries(blankRatings).map(([categoryId, rating]) => {
      const previousRating = previousRatings[Number(categoryId)]

      if (previousRating?.rating == null) return [categoryId, rating]

      return [
        categoryId,
        {
          ...rating,
          rating: previousRating.rating,
          cause: previousRating.cause,
          rootCause: previousRating.rootCause,
        },
      ]
    }),
  ) as T
}

/**
 * Drafts can contain ratings from scorecards that are not currently visible.
 * Keep those previous values while allowing current scorecard categories to overwrite or clear their own values.
 */
export const mergeDraftRatingValues = (currentRatings: RatingFields[], previousDraftRatings: RatingFields[]) => {
  const currentRatingCategoryIds = new Set(currentRatings.map((rating) => rating.categoryId))
  const currentRatedRatings = currentRatings.filter((rating) => rating.rating != null)
  const preservedRatings = previousDraftRatings.filter(
    (rating) => rating.rating != null && !currentRatingCategoryIds.has(rating.categoryId),
  )

  return [...currentRatedRatings, ...preservedRatings]
}

export const shouldRemoveReviewDraft = ({
  hasComment,
  canReview,
  hasDraftRatings,
  hasGivenRatings = hasDraftRatings,
}: {
  hasComment: boolean
  canReview: boolean
  hasDraftRatings: boolean
  hasGivenRatings?: boolean
}) => !hasComment && (!canReview || !hasDraftRatings || !hasGivenRatings)

export const groupCategories = <T extends { id: number; name: string; position: number }>(
  categories: Rating[],
  groups: T[],
  keepEmptyGroups = false,
): CategoryGroup[] => {
  const ungroupedCategories = categories
    .filter((cat) => !cat.groupId)
    .sort((a, b) => (a.position || 0) - (b.position || 0))
  return [
    getUngroupedCategoryGroup(ungroupedCategories),
    ...groups
      .map((group) => ({
        ...group,
        categories: categories
          .filter((cat) => cat.groupId === group.id)
          .sort((a, b) => (a.position || 0) - (b.position || 0)),
      }))
      .filter((group) => keepEmptyGroups || group.categories.length),
  ]
}

export function buildRatingsMap({
  ratings,
  categoryOptions,
  preferRatingValues,
}: {
  ratings: RatingTypeFromServices[]
  categoryOptions: Record<number, Category>
  preferRatingValues: boolean
}): GivenRatings {
  return ratings.reduce((ratingsMap, rating) => {
    const category = categoryOptions[rating.categoryId] as Category | undefined

    if (!category) return ratingsMap
    const commonProperties = {
      scale: `SCALE_${rating.ratingMax + 1}`,
      critical: rating.critical,
      rating: fromApiRating(rating.rating),
      cause: rating.cause,
      rootCause: rating.rootCause,
    }

    // if preferRatingValues = true -> return category config that was used while rating and adds the rating values
    // if preferRatingValues = false -> returns the latest category config (updated descriptions, root causes, etc.) and adds the rating values
    if (preferRatingValues) {
      ratingsMap[rating.categoryId] = {
        ...category,
        ...commonProperties,
      }
    } else {
      ratingsMap[rating.categoryId] = {
        ...commonProperties,
        ...category,
      }
    }

    return ratingsMap
  }, {})
}

export const isRated = (r?: { rating?: RatingValue | null }) =>
  r && r.rating !== null && r.rating !== undefined && !isNaRating(r.rating)

/**
 * `isRated` for a submitted rating from an API response, where `null` means N/A.
 */
export const isApiRated = (r?: { rating?: number | null }) => isRated({ rating: fromApiRating(r?.rating) })

export const isValidComment = (comment: string) => {
  const content = comment.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, '').replace(/&#39;/g, `'`)
  return !!content.trim().length
}

export const hasRootCauses = (cause: string[] | undefined) => !!cause?.length

export const isNegativeRating = (rating: number, scale: Scale[]) => rating < Math.floor((scale?.length ?? 0) / 2)

export const isNeutralRating = (rating: number, scale: Scale[]) => {
  if (scale.length % 2 !== 1) {
    // Array does not have a neutral rating (i.e. it's either a binary or 4-point scale)
    return false
  }

  const neutralRatingIndex = Math.floor(scale.length / 2)
  const neutralRating = scale[neutralRatingIndex].label - 1

  return neutralRating === rating
}

export const isRootCauseRequiredForRating = (rating: number, ratingCategory: Rating): boolean => {
  const { requireReason, requireReasonVisibility, scale, isAdvancedScorecardCategory } = ratingCategory

  if (!requireReason) return false
  if (isAdvancedScorecardCategory) return isReasonRequiredForRating(rating, ratingCategory.advancedRootCauses)

  // legacy category
  if (requireReasonVisibility === RequireReasonVisibility.All) return true

  if (requireReasonVisibility === RequireReasonVisibility.NegativeAndNeutral) {
    return isNegativeRating(rating, SCALE_VALUES[scale]) || isNeutralRating(rating, SCALE_VALUES[scale])
  }

  return isNegativeRating(rating, SCALE_VALUES[scale])
}

export const isReasonRequiredForRatingLegacy = (
  rating: number,
  scale: Scale[],
  requireReasonVisibility: RequireReasonVisibility,
): boolean => {
  switch (requireReasonVisibility) {
    case RequireReasonVisibility.All:
      return true
    case null:
    case RequireReasonVisibility.Negative: {
      return isNegativeRating(rating, scale!)
    }
    case RequireReasonVisibility.NegativeAndNeutral: {
      const negativeRating = isNegativeRating(rating, scale!)
      const neutralRating = isNeutralRating(rating, scale!)
      return negativeRating || neutralRating
    }
    default:
      return false
  }
}

const getRootCausesRating = (rating: RatingValue | null | undefined, rootCauses: RootCauses | undefined) => {
  if (typeof rating !== 'number') return undefined

  const rootCausesRating = rootCauses?.ratings.find((r) => r.rating === rating)
  if (!rootCausesRating) return undefined

  return rootCausesRating
}

type RootCausesRating = RootCauses['ratings'][number]
type MultiTierRootCause = NonNullable<RootCausesRating['multiTierRootCause']>
type MultiTierRootCauseTier = MultiTierRootCause['tiers'][number]
interface NamedRootCause {
  id?: number | string
  name?: string
  cause?: string
}
interface ConfiguredRootCause {
  id?: number | string
  cause: string
}
interface SelectableRootCause {
  id: string
  cause: string
}

const getRootCauseLabel = (rootCause: NamedRootCause) => rootCause.name?.trim() || rootCause.cause?.trim()
const isRootCauseLabel = (label: string | undefined): label is string => !!label
const isConfiguredRootCause = (rootCause: ConfiguredRootCause | undefined): rootCause is ConfiguredRootCause =>
  !!rootCause
const isSelectableRootCause = (rootCause: SelectableRootCause | undefined): rootCause is SelectableRootCause =>
  !!rootCause
const isFreeTextAllowedForRootCausesRating = (rootCausesRating: RootCausesRating, fallback = false) => {
  if ('freeTextAllowed' in rootCausesRating) return rootCausesRating.freeTextAllowed === true

  return fallback
}
const isFreeTextAllowedForRating = (
  rating: RatingValue | null | undefined,
  advancedRootCauses: RootCauses | undefined,
  freeTextAllowed = false,
) => {
  const rootCausesRating = getRootCausesRating(rating, advancedRootCauses)
  const globalFreeTextAllowed = freeTextAllowed || !!advancedRootCauses?.freeTextAllowed

  return rootCausesRating
    ? isFreeTextAllowedForRootCausesRating(rootCausesRating, globalFreeTextAllowed)
    : globalFreeTextAllowed
}

const toConfiguredRootCause = (rootCause: NamedRootCause): ConfiguredRootCause | undefined => {
  const cause = getRootCauseLabel(rootCause)

  return cause ? { id: rootCause.id, cause } : undefined
}

const toSelectableRootCause = (rootCause: ConfiguredRootCause): SelectableRootCause | undefined => {
  return hasSelectableRootCauseId(rootCause.id) ? { id: String(rootCause.id), cause: rootCause.cause } : undefined
}

const getTierRootCauseOptions = (tiers: MultiTierRootCauseTier[]): ConfiguredRootCause[] =>
  tiers.flatMap((tier) => [
    ...(tier.rootCauses || []).map(toConfiguredRootCause).filter(isConfiguredRootCause),
    ...getTierRootCauseOptions(tier.subtiers || []),
  ])

const getConfiguredRootCauseOptions = (rootCausesRating: RootCausesRating): ConfiguredRootCause[] => [
  ...(rootCausesRating.rootCauses || []).map(toConfiguredRootCause).filter(isConfiguredRootCause),
  ...(rootCausesRating.multiTierRootCause?.looseRootCauses || [])
    .map(toConfiguredRootCause)
    .filter(isConfiguredRootCause),
  ...getTierRootCauseOptions(rootCausesRating.multiTierRootCause?.tiers || []),
]

const getConfiguredRootCauseLabels = (rootCausesRating: RootCausesRating) =>
  getConfiguredRootCauseOptions(rootCausesRating).map(({ cause }) => cause)

const getConfiguredSelectableRootCauseOptions = (rootCausesRating: RootCausesRating) =>
  getConfiguredRootCauseOptions(rootCausesRating).map(toSelectableRootCause).filter(isSelectableRootCause)

const normalizeRootCauseForComparison = (cause: string) => cause.trim()

export const getReasonStringsFromRootCause = (rootCause?: RatingRootCause) => {
  if (!rootCause) return []

  const predefinedReasons = (rootCause.predefined || []).map(({ cause }) => cause)
  const otherReason = rootCause.other?.trim()
  const otherReasons = otherReason ? [otherReason] : []

  return [...predefinedReasons, ...otherReasons].filter(isRootCauseLabel)
}

export const getConfiguredRootCauseLabelsForRating = (
  rating: RatingValue | null | undefined,
  rootCauses: RootCauses | undefined,
): string[] => {
  const rootCausesRating = getRootCausesRating(rating, rootCauses)

  return rootCausesRating ? getConfiguredRootCauseLabels(rootCausesRating) : []
}

export const getRootCausesForRating = (rating: RatingValue | null | undefined, rootCauses: RootCauses) => {
  return getRootCausesRating(rating, rootCauses)?.rootCauses || []
}

interface RatingRootCauseDerivationSource {
  rating?: RatingValue | null
  cause?: string[] | null
  advancedRootCauses?: RootCauses
  freeTextAllowed?: boolean
}

interface DraftRatingSource extends RatingRootCauseDerivationSource {
  categoryId: number
  rootCause?: RatingRootCause
}

export const deriveRatingRootCauseFromCause = ({
  rating,
  cause,
  advancedRootCauses,
  freeTextAllowed = false,
}: RatingRootCauseDerivationSource): RatingRootCause | undefined => {
  if (typeof rating !== 'number' || !cause?.length) return undefined

  const rootCausesRating = getRootCausesRating(rating, advancedRootCauses)
  if (!rootCausesRating) return undefined

  const configuredRootCauseOptions = getConfiguredRootCauseOptions(rootCausesRating)
  const configuredRootCauseLabels = new Set(configuredRootCauseOptions.map(({ cause }) => cause))
  const configuredSelectableRootCauses = getConfiguredSelectableRootCauseOptions(rootCausesRating)

  const predefined: RatingRootCause['predefined'] = []
  const predefinedIds = new Set<string>()
  const otherRootCauses: string[] = []
  const otherRootCauseLabels = new Set<string>()
  const canSaveOtherRootCause = isFreeTextAllowedForRating(rating, advancedRootCauses, freeTextAllowed)

  cause.forEach((selectedRootCause) => {
    const selectedCause = selectedRootCause.trim()
    if (!selectedCause) return

    const configuredRootCauses = configuredSelectableRootCauses.filter((rootCause) => rootCause.cause === selectedCause)
    const configuredRootCause = configuredRootCauses.length === 1 ? configuredRootCauses[0] : undefined
    if (configuredRootCause) {
      if (predefinedIds.has(configuredRootCause.id)) return

      predefinedIds.add(configuredRootCause.id)
      predefined.push(configuredRootCause)
      return
    }

    const hasAmbiguousConfiguredRootCauseLabel = configuredRootCauses.length > 1
    const shouldSkipOtherRootCause =
      !canSaveOtherRootCause || (configuredRootCauseLabels.has(selectedCause) && !hasAmbiguousConfiguredRootCauseLabel)

    if (shouldSkipOtherRootCause) return
    if (otherRootCauseLabels.has(selectedCause)) return

    otherRootCauseLabels.add(selectedCause)
    otherRootCauses.push(selectedCause)
  })

  if (!predefined.length && !otherRootCauses.length) return undefined

  return {
    predefined,
    other: otherRootCauses.join('; '),
  }
}

export const normalizeDraftRatingCause = (cause?: string[] | null): string[] => cause ?? []

export const toDraftRatingFields = (rating: DraftRatingSource): DraftRatingFields => {
  const cause = normalizeDraftRatingCause(rating.cause)
  const rootCause = rating.rootCause ?? deriveRatingRootCauseFromCause({ ...rating, cause })

  return {
    cause,
    ...(rootCause !== undefined ? { rootCause } : {}),
    rating: rating.rating ?? undefined,
    categoryId: rating.categoryId,
  }
}

export const isReasonRequiredForRating = (
  rating: RatingValue | null | undefined,
  rootCauses: RootCauses | undefined,
): boolean => {
  if (!rootCauses) return false

  const rootCausesForRating = getRootCausesRating(rating, rootCauses)

  if (!rootCausesForRating) return false

  return (
    getConfiguredRootCauseLabels(rootCausesForRating).length > 0 ||
    isFreeTextAllowedForRootCausesRating(rootCausesForRating, rootCauses.freeTextAllowed)
  )
}

export const getUngroupedCategoryGroup = (categories: Category[]): CategoryGroup => ({
  id: 0,
  name: 'Ungrouped',
  position: 0,
  categories,
})

type GrammarDescriptionFallbackRating = 0 | 1 | 2 | 3 | 4

const grammarDescriptionFallbackKeys: Record<GrammarDescriptionFallbackRating, string> = {
  0: 'conversations.sidebar.auto_qa.highlight_mistakes.grammar.0',
  1: 'conversations.sidebar.auto_qa.highlight_mistakes.grammar.1',
  2: 'conversations.sidebar.auto_qa.highlight_mistakes.grammar.2',
  3: 'conversations.sidebar.auto_qa.highlight_mistakes.grammar.3',
  4: 'conversations.sidebar.auto_qa.highlight_mistakes.grammar.4',
}

const isGrammarDescriptionFallbackRating = (value: number): value is GrammarDescriptionFallbackRating =>
  Number.isInteger(value) && value >= 0 && value <= 4

const getGrammarDescriptionFallback = (rating: GrammarDescriptionFallbackRating) =>
  i18n.t(grammarDescriptionFallbackKeys[rating])

// if it's legacy, it will return the causes as they are
// if freeTextAllowed is true, it will return the causes as they are
// if freeTextAllowed is false, it will filter out any cause that is not in the category's advanced root causes
export const getValidRootCauses = (
  rating: RatingValue | null | undefined,
  causes: string[] | undefined,
  advancedRootCauses: RootCauses | undefined,
  freeTextAllowed = false,
  legacy = true,
): string[] => {
  if (!causes || causes.length === 0) return []
  if (legacy) return causes

  const rootCausesForRating = getRootCausesRating(rating, advancedRootCauses)
  const canKeepFreeTextCause = isFreeTextAllowedForRating(rating, advancedRootCauses, freeTextAllowed)

  if (canKeepFreeTextCause) return causes
  if (!rootCausesForRating) return []

  const existingRootCauses = new Set(
    getConfiguredRootCauseLabels(rootCausesForRating).map(normalizeRootCauseForComparison),
  )
  return causes.filter((c: string) => existingRootCauses.has(normalizeRootCauseForComparison(c)))
}

export const getAutoQaDescription = (
  autoQaRatingCategory: AutoQaRatingCategory | undefined,
  category: Category | undefined,
  ratingMax?: number,
) => {
  if (!autoQaRatingCategory?.model) return ''
  if (category?.autoQaCustomCategoryTemplateId) return i18n.t('conversations.sidebar.auto_qa.custom_category_popup')

  const model = autoQaRatingCategory.model === 'openai' ? 'open_ai' : 'klaus_model'
  const rating = autoQaRatingCategory.rating
  // N/A descriptions are stored under the translation key segment '42'; it is a key name, not a rating value
  const ratingKey = isAutoQaNaRating(rating) ? '42' : rating
  const categoryType = category?.autoQaCategory
  const mistakes = autoQaRatingCategory.mistakes
  // Template scores store N/A as '42' while AutoQA may return `null`, so compare normalized values
  // TODO(ZQAZOS-704): remove the normalization once template scores no longer use 42 for N/A (it relies on the '42' fallback)
  const categoryRating = category?.categoryRatings?.find(
    (r) => fromAutoQaRating(String(r.rating)) === fromAutoQaRating(rating),
  )

  if (categoryType === AutoQaCategoryType.Prompt && categoryRating) return categoryRating.description

  const effectiveRatingMax = ratingMax ?? autoQaRatingCategory.ratingMax

  let descriptionKey: string
  if (categoryType === AutoQaCategoryType.Readability && rating === '1' && mistakes.length) {
    descriptionKey = `settings.workspaces.rating_categories.edit.auto_qa_rating_descriptions.${model}.${categoryType}.${rating}.${mistakes[0].name}`
  } else if (categoryType === AutoQaCategoryType.Grammar && Number(rating) === effectiveRatingMax) {
    descriptionKey = `settings.workspaces.rating_categories.edit.auto_qa_rating_descriptions.${model}.${categoryType}.4_v2`
  } else {
    descriptionKey = `settings.workspaces.rating_categories.edit.auto_qa_rating_descriptions.${model}.${categoryType}.${ratingKey}`
  }

  if (translationExists(descriptionKey)) return i18n.t(descriptionKey)
  if (categoryType === AutoQaCategoryType.Grammar && !isAutoQaNaRating(rating)) {
    const numericRating = Number(rating)
    const grammarFallbackRating = numericRating === effectiveRatingMax ? 4 : numericRating

    if (isGrammarDescriptionFallbackRating(grammarFallbackRating)) {
      return getGrammarDescriptionFallback(grammarFallbackRating)
    }
  }
  if (isAutoQaNaRating(rating)) return i18n.t('conversations.sidebar.auto_qa.na_popup')
  return ''
}

const getRequestRootCause = ({
  rating,
  rootCause,
  advancedRootCauses,
  freeTextAllowed,
  multipleRequireReasons,
}: Rating): RatingRootCause | undefined => {
  if (!rootCause) return undefined

  if (multipleRequireReasons === false) {
    return getSingleSelectRequestRootCause({ rating, rootCause, advancedRootCauses, freeTextAllowed })
  }

  const other = rootCause.other?.trim()
  const canKeepOther =
    !other ||
    typeof rating !== 'number' ||
    !advancedRootCauses ||
    isFreeTextAllowedForRating(rating, advancedRootCauses, freeTextAllowed)
  const sanitizedRootCause = canKeepOther ? rootCause : { ...rootCause, other: '' }

  if (sanitizedRootCause.predefined?.length || sanitizedRootCause.other?.trim()) return sanitizedRootCause

  return undefined
}

const findUniqueRootCauseByCause = (rootCauses: SelectableRootCause[], cause: string) => {
  const matches = rootCauses.filter((rootCause) => rootCause.cause === cause)
  return matches.length === 1 ? matches[0] : undefined
}

const getRetainedPredefinedRootCauseForRequest = ({
  rating,
  rootCause,
  advancedRootCauses,
}: Pick<Rating, 'rating' | 'rootCause' | 'advancedRootCauses'>) => {
  const rootCausesRating = typeof rating === 'number' ? getRootCausesRating(rating, advancedRootCauses) : undefined
  const configuredRootCauses = rootCausesRating ? getConfiguredSelectableRootCauseOptions(rootCausesRating) : undefined

  for (const { id, cause } of rootCause?.predefined || []) {
    const trimmedCause = cause?.trim()
    if (!trimmedCause) continue

    if (!configuredRootCauses) {
      if (hasSelectableRootCauseId(id)) return { id: String(id), cause: trimmedCause }
      continue
    }

    if (!hasSelectableRootCauseId(id)) {
      const configuredRootCause = findUniqueRootCauseByCause(configuredRootCauses, trimmedCause)
      if (configuredRootCause) return configuredRootCause
      continue
    }

    const rootCauseId = String(id)
    if (configuredRootCauses.some((rootCause) => rootCause.id === rootCauseId)) {
      return { id: rootCauseId, cause: trimmedCause }
    }
  }

  return undefined
}

const getSingleSelectRequestRootCause = ({
  rating,
  rootCause,
  advancedRootCauses,
  freeTextAllowed,
}: Pick<Rating, 'rating' | 'rootCause' | 'advancedRootCauses' | 'freeTextAllowed'>): RatingRootCause | undefined => {
  if (!rootCause) return undefined

  const retainedPredefinedRootCause = getRetainedPredefinedRootCauseForRequest({
    rating,
    rootCause,
    advancedRootCauses,
  })

  if (retainedPredefinedRootCause) {
    return {
      predefined: [retainedPredefinedRootCause],
      other: '',
    }
  }

  const other = rootCause.other?.trim()
  const canKeepOther =
    !!other &&
    (typeof rating !== 'number' ||
      !advancedRootCauses ||
      isFreeTextAllowedForRating(rating, advancedRootCauses, freeTextAllowed))

  if (!canKeepOther) return undefined

  return {
    predefined: [],
    other,
  }
}

const getRequestCause = ({ rating, cause, advancedRootCauses, freeTextAllowed }: Rating) => {
  if (typeof rating !== 'number' || !advancedRootCauses || !cause?.length) return cause

  return getValidRootCauses(rating, cause, advancedRootCauses, freeTextAllowed, false)
}

export const toRatingRequestPayload = (rating: Rating, includeRootCause = false): RatingRequestPayload => {
  const { categoryId, rating: ratingValue, cause } = rating

  if (!includeRootCause) {
    return {
      categoryId,
      rating: toApiRating(ratingValue),
      cause,
    }
  }

  const rootCause = getRequestRootCause(rating)
  const requestCause =
    rating.multipleRequireReasons === false && rating.rootCause
      ? getReasonStringsFromRootCause(rootCause)
      : getRequestCause(rating)

  return {
    categoryId,
    rating: toApiRating(ratingValue),
    ...(rootCause ? { rootCause } : {}),
    cause: requestCause,
  }
}
